// ---------------------------------------------------------------------------
// EmptyTabPlaceholder
//
// Reusable scaffold card shown inside a Management View tab while the real
// content for that tab is still under construction. Each call site labels the
// phase the content is expected to land in plus a short message.
//
// Phase 0 only — these placeholder calls are deleted as their tabs come
// online (Phase 1 = Quality Control, Phase 2 = Overview, Phase 3 = locked
// tabs). Keep the component minimal to avoid bit-rot.
// ---------------------------------------------------------------------------

import { Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';

interface EmptyTabPlaceholderProps {
  /** Phase number this tab's content is expected to ship in (1, 2, 3, ...). */
  phase: number;
  /** Short human-readable message describing what is coming. */
  message: string;
  /** Additional CSS classes on the wrapping card. */
  className?: string;
}

/**
 * Centred, muted-foreground placeholder used by every Phase-0 Management
 * View tab. Renders a "Phase N" eyebrow above the message so reviewers can
 * tell at a glance which phase a tab still needs.
 */
export function EmptyTabPlaceholder({
  phase,
  message,
  className,
}: Readonly<EmptyTabPlaceholderProps>) {
  return (
    <Card
      className={cn('border-dashed', className)}
      data-testid="management-view-empty-tab-placeholder"
    >
      <CardContent className="flex flex-col items-center justify-center gap-2 p-12 text-center">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Phase {phase}
        </span>
        <p className="text-sm text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );
}
